import { useMemo, useEffect, useRef, useState } from 'react';
import { computeHeadToHeadData, buildHeadToHeadPayload, metricVal, phaseFor } from '../../utils/headToHead';
import { convertValue, getDisplayUnit } from '../../utils/unitConvert';
import { exportHeadToHeadPng } from '../../utils/headToHeadExport';
import { publishZoom } from '../../utils/chartZoomBus';
import { publishCornerHighlight } from '../../hooks/useCornerHighlight';
import { LAP_COLORS } from '../LapSelector/LapSelector';
import { SF_MISMATCH_WARN_M } from '../../utils/sfAlignment';
import PanelHeader from '../WindowFrame/headerSlot';
import HelpButton from '../HelpButton/HelpButton';
import { streamOllamaChat, fetchOllamaModels, pickCoachModel } from '../../utils/llmStream';
import { DEEP_CHAT_MIN_CONTEXT } from '../../utils/deepAnalysisChat';
import MiniMarkdown, { splitMarkdownSections } from '../../utils/miniMarkdown';
import { TrackDifferenceView } from './TrackDifference';
import styles from './HeadToHead.module.css';

const EPS_S = 0.05;   // deltas below this are noise — no badge, muted color
const M_TO_FT = 3.28084;

// Technique metrics diffed against the reference driver inside an expanded
// corner row. Polarity and wording mirror MetricsPanel's METRIC_DEFS.
const TECH_DEFS = [
  { key: 'min_speed',             label: 'Min speed',       unit: 'mph', higherIsBetter: true,
    tip: 'Lowest speed in the corner — higher = more speed carried through' },
  { key: 'exit_speed',            label: 'Exit speed',      unit: 'mph', higherIsBetter: true,
    tip: 'Speed 75 m past the apex — sets the run down the next straight' },
  { key: 'brake_app_dist_m',      label: 'Brake point',     unit: 'm',   higherIsBetter: false,
    tip: 'Metres before the apex where braking begins — lower = braking later' },
  { key: 'throttle_pickup_m',     label: 'Throttle pickup', unit: 'm',   higherIsBetter: false,
    tip: 'Metres past the apex where the throttle opens — lower = back to power earlier' },
  { key: 'coast_s',               label: 'Coast',           unit: 's',   higherIsBetter: false, phase: true,
    tip: 'Seconds neither braking nor accelerating in this corner — the classic time leak' },
  { key: 'brake_release_dist_m',  label: 'Release',         unit: 'm',   higherIsBetter: true,  phase: true,
    tip: 'Metres over which braking tapers from peak to fully released — longer = smoother, progressive release (>22 m); short = snapped off abruptly (<10 m), unsettling the car' },
  { key: 'trail_brake_overlap_m', label: 'Trail overlap',   unit: 'm',   higherIsBetter: null,
    tip: 'Metres of simultaneous braking and turning — trail-braking depth (style, not better/worse)' },
  { key: 'peak_brake_g',          label: 'Peak brake',      unit: 'G',   higherIsBetter: true,
    tip: 'Maximum deceleration force — higher = braking harder' },
];

const fmtDelta = (s, digits = 3) =>
  s == null ? '—' : `${s >= 0 ? '+' : '−'}${Math.abs(s).toFixed(digits)}`;

// Outside-click dismissal for the AI feedback driver menu (same pattern as
// SessionHeader's header dropdowns).
function useDismiss(open, setOpen) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, setOpen]);
  return ref;
}

// Cross-panel wiring: hovering a corner row highlights it on the traces + map;
// the ⌖ button zooms every trace to the corner band and refits the map.
const rowHighlightDetail = (row) => ({
  cornerId: row.corner.id,
  label: row.corner.label || `T${row.corner.id}`,
  lat: row.corner.apex_lat ?? row.corner.lat,
  lon: row.corner.apex_lon ?? row.corner.lon,
  ...(row.x > row.e ? { distFtMin: row.e * M_TO_FT, distFtMax: row.x * M_TO_FT } : {}),
});

function zoomToRow(row) {
  if (!(row.x > row.e)) return;
  const pad = (row.x - row.e) * 0.15;
  const distFtMin = Math.max(0, row.e - pad) * M_TO_FT;
  const distFtMax = (row.x + pad) * M_TO_FT;
  publishZoom([distFtMin, distFtMax]);           // zooms all traces
  window.dispatchEvent(new CustomEvent('trackapp:chart-zoom', {
    detail: { distFtMin, distFtMax },            // refits the track map
  }));
}

function DeltaCell({ delta, badge, barFrac, title }) {
  if (delta == null) return <td className={styles.deltaCell}>—</td>;
  const losing = delta > EPS_S;
  const gaining = delta < -EPS_S;
  return (
    <td className={styles.deltaCell} title={title}>
      <div className={styles.deltaWrap}>
        <div className={styles.barTrack}>
          <div
            className={`${styles.bar} ${losing ? styles.barLoss : gaining ? styles.barGain : styles.barFlat}`}
            style={losing
              ? { left: '50%', width: `${Math.min(50, barFrac * 50)}%` }
              : { right: '50%', width: `${Math.min(50, barFrac * 50)}%` }}
          />
        </div>
        <span className={losing ? styles.loss : gaining ? styles.gain : styles.flat}>
          {fmtDelta(delta)}
        </span>
        {badge && <span className={styles.badge}>{badge}</span>}
      </div>
    </td>
  );
}

export default function HeadToHead({
  corners = [],
  sectors = [],
  elevationProfile = [],
  lapTraces, selectedLaps, session, driverName1,
  metrics, metricsLoading, onCalculate,
  compareDrivers = [],
  unitPrefs = {},
  trackName = null, sessionDate = null,
  primarySfOffsetM = null,
  onCoach = null,
  onAiSummary = null,
  on3DCompare = null,
  onOpenTrackDifference = null,
  // Same served model + num_ctx the debrief uses. Sharing them matters: this
  // panel runs the SAME model, so a different num_ctx here unloads and reloads
  // it every time the driver switches between the two features.
  coachModel = {},
}) {
  const [sortByLoss, setSortByLoss] = useState(false);
  const [expandedCorner, setExpandedCorner] = useState(null);
  const [trackDifferenceOpen, setTrackDifferenceOpen] = useState(false);
  const [trackOpponentKey, setTrackOpponentKey] = useState(null);
  const panelRef = useRef(null);

  // Largest S/F-origin offset among the loaded drivers (metres) — deltas are
  // unreliable while any session's laps start elsewhere on track.
  const sfMismatch = [primarySfOffsetM, ...compareDrivers.map(d => d.sfOffsetM)]
    .filter(v => v != null && v >= SF_MISMATCH_WARN_M)
    .reduce((a, v) => Math.max(a ?? 0, v), null);

  // ── All derivation (drivers, reference, per-corner deltas) lives in the
  // shared util so App.jsx can feed the same data to the AI briefing.
  const { drivers, ready, ref, others, rows, straightsRow, straightRows, maxAbs } = useMemo(
    () => computeHeadToHeadData({
      primary: { name: driverName1, accent: LAP_COLORS[0], session, lapTraces,
                 selectedLaps, metrics, metricsLoading, onCalculate },
      compareDrivers, corners,
    }),
    [session, driverName1, lapTraces, selectedLaps, metrics, metricsLoading,
     onCalculate, compareDrivers, corners]);

  // Head-to-Head remains an N-driver report, while the visual comparison is
  // deliberately pairwise. Keep the primary driver fixed when available and
  // let the user choose which rival to inspect.
  const trackDriverA = ready.find(d => d.key === 'p') || ref || null;
  const trackOpponents = ready.filter(d => d.key !== trackDriverA?.key);
  const trackDriverB = trackOpponents.find(d => d.key === trackOpponentKey)
    || (ref?.key !== trackDriverA?.key ? ref : null)
    || [...trackOpponents].sort((a, b) => a.best.trace.lap_time_seconds - b.best.trace.lap_time_seconds)[0]
    || null;
  useEffect(() => {
    if (trackDriverB && trackDriverB.key !== trackOpponentKey) setTrackOpponentKey(trackDriverB.key);
  }, [trackDriverB, trackOpponentKey]);

  const trackLap = (driver) => driver ? ({
    key: `${driver.session.session_id}:${driver.best.lapIndex}`,
    driverKey: driver.key,
    sessionId: driver.session.session_id,
    lapIndex: driver.best.lapIndex,
    lapNo: driver.lapNo,
    name: driver.name,
    accent: driver.accent,
    trace: driver.best.trace,
  }) : null;
  const trackLapA = trackLap(trackDriverA);
  const trackLapB = trackLap(trackDriverB);

  // ── Auto-compute Corner Metrics for drivers missing them ─────────────────
  const calcFiredRef = useRef({});
  useEffect(() => {
    if (!corners.length) return;
    drivers.forEach(d => {
      if (!d.onCalculate || d.metrics || d.metricsLoading) return;
      if (!(d.selectedLaps || []).some(i => i >= 0)) return;
      const k = `${d.key}:${(d.selectedLaps || []).join(',')}`;
      if (calcFiredRef.current[k]) return;
      calcFiredRef.current[k] = true;
      d.onCalculate(false);   // revealPanel=false — stay on this panel
    });
  }, [drivers, corners]);

  // ── Inline AI coach (regular-user flow) ──────────────────────────────────
  // One click: build the head-to-head briefing behind the scenes, stream the
  // Ollama response into a card in this panel. Prompts are never shown — the
  // debug PromptPanel (admin) remains the place to inspect them.
  // `focus` = { key, name } of the driver the coaching addresses — usually the
  // primary (logged-in) driver, but any compared driver can be picked from the
  // button's menu (e.g. coach a slower guest against the primary's lap).
  const [coach, setCoach] = useState(null);   // { status: building|streaming|done|error, text, focus }
  const [aiMenuOpen, setAiMenuOpen] = useState(false);
  const aiMenuRef = useDismiss(aiMenuOpen, setAiMenuOpen);
  const coachAbortRef = useRef(null);
  const coachBodyRef  = useRef(null);
  const coachBusy = coach?.status === 'building' || coach?.status === 'streaming';

  const runAiCoach = async (focus) => {
    if (!onAiSummary) return;
    setAiMenuOpen(false);
    coachAbortRef.current?.abort();
    const controller = new AbortController();
    coachAbortRef.current = controller;
    setCoach({ status: 'building', text: '', focus });
    try {
      const payload = buildHeadToHeadPayload({ ready, ref, rows, straightsRow, straightRows, focusKey: focus?.key });
      if (!payload) throw new Error('nothing to compare');
      const [briefing, models] = await Promise.all([onAiSummary(payload), fetchOllamaModels()]);
      if (controller.signal.aborted) return;
      if (!briefing?.system_prompt || !briefing?.user_prompt) throw new Error('briefing unavailable');
      const model = pickCoachModel(models, coachModel.model);
      if (!model) throw new Error('no Ollama model installed');
      setCoach({ status: 'streaming', text: '', focus });
      let acc = '';
      await streamOllamaChat({
        model,
        system: briefing.system_prompt,
        user: briefing.user_prompt,
        minContext: coachModel.numCtx || DEEP_CHAT_MIN_CONTEXT,
        signal: controller.signal,
        onToken: t => { acc += t; setCoach({ status: 'streaming', text: acc, focus }); },
      });
      if (controller.signal.aborted) return;
      setCoach({ status: 'done', text: acc, focus });
    } catch (err) {
      if (err.name === 'AbortError' || controller.signal.aborted) return;
      console.error('AI coach failed:', err);
      setCoach({ status: 'error', text: '', focus });
    }
  };

  // Stop keeps whatever streamed so far; Dismiss clears the card entirely.
  const stopCoach = () => {
    coachAbortRef.current?.abort();
    setCoach(c => (c?.text ? { ...c, status: 'done' } : null));
  };
  const dismissCoach = () => {
    coachAbortRef.current?.abort();
    setCoach(null);
  };

  // Follow the stream as tokens arrive; cancel any in-flight call on unmount.
  useEffect(() => {
    if (coachBusy && coachBodyRef.current) {
      coachBodyRef.current.scrollTop = coachBodyRef.current.scrollHeight;
    }
  }, [coach, coachBusy]);
  useEffect(() => () => coachAbortRef.current?.abort(), []);

  // Top-3 badge numbers per driver column (largest losses only).
  const badges = useMemo(() => {
    const map = new Map(); // `${driverKey}:${cornerId}` -> 1..3
    for (const d of others) {
      rows
        .map(r => ({ cid: r.corner.id, t: r.cells.find(cl => cl.driver === d)?.total }))
        .filter(x => x.t != null && x.t > EPS_S)
        .sort((a, b) => b.t - a.t)
        .slice(0, 3)
        .forEach((x, i) => map.set(`${d.key}:${x.cid}`, i + 1));
    }
    return map;
  }, [rows, others]);

  const displayRows = useMemo(() => {
    if (!sortByLoss) return rows;
    // Biggest *magnitude* first — losses and gains are both interesting.
    const size = r => Math.max(...r.cells.map(cl => Math.abs(cl.total ?? 0)));
    return [...rows].sort((a, b) => size(b) - size(a));
  }, [rows, sortByLoss]);

  // ── Signature strip values ────────────────────────────────────────────────
  const signature = (d) => {
    const env = d.metrics?.session_envelope;
    let coast = null;
    const releases = [];
    const taperDists = [];
    if (d.metrics?.by_corner) {
      for (const cid of Object.keys(d.metrics.by_corner)) {
        const pl = d.metrics.by_corner[cid].per_lap?.find(p => p.lap_index === d.best?.lapIndex);
        if (pl?.phase?.coast_s != null) coast = (coast ?? 0) + pl.phase.coast_s;
        if (pl?.phase?.brake_release_type) releases.push(pl.phase.brake_release_type);
        if (pl?.phase?.brake_release_dist_m != null) taperDists.push(pl.phase.brake_release_dist_m);
      }
    }
    const mode = releases.length
      ? [...releases.reduce((m, r) => m.set(r, (m.get(r) || 0) + 1), new Map()).entries()]
          .sort((a, b) => b[1] - a[1])[0][0]
      : null;
    const sorted = [...taperDists].sort((a, b) => a - b);
    const medianTaper = sorted.length
      ? sorted[(sorted.length - 1) >> 1] : null;
    return { env, coast, release: mode, medianTaper };
  };

  const speedUnit = getDisplayUnit('mph', unitPrefs);
  const fmtSpeed = v => v == null ? '—' : `${convertValue(v, 'mph', unitPrefs).toFixed(1)}`;

  // Display-ready value + colored diff vs reference for one (driver, corner,
  // metric) — shared by the on-screen cells and the PNG export.
  const techCellData = (d, cid, def) => {
    const v = metricVal(d, cid, def);
    if (v == null) return { text: d.metricsLoading ? '…' : '—', diffText: null, good: null };
    const isSpeed = def.unit === 'mph';
    const text = isSpeed ? fmtSpeed(v) : v.toFixed(def.unit === 'G' ? 2 : 1);
    const rv = metricVal(ref, cid, def);
    if (rv == null || d === ref) return { text, diffText: null, good: null };
    const diff = v - rv;
    const shownDiff = isSpeed ? (convertValue(v, 'mph', unitPrefs) - convertValue(rv, 'mph', unitPrefs)) : diff;
    const good = def.higherIsBetter == null ? null : (def.higherIsBetter ? diff > 0 : diff < 0);
    return { text, diffText: fmtDelta(shownDiff, def.unit === 'G' ? 2 : 1), good };
  };

  const techCell = (d, cid, def) => {
    const { text, diffText, good } = techCellData(d, cid, def);
    return (
      <td key={def.key} className={styles.techVal}>
        {text}
        {diffText && (
          <span className={good == null ? styles.flat : good ? styles.gain : styles.loss}>
            {' '}{diffText}
          </span>
        )}
      </td>
    );
  };

  // ── PNG export: same derived data, drawn as a report card. The badged
  // (top time-loss) corners are exported expanded regardless of UI state.
  const handleExportPng = () => {
    if (!ref || ready.length < 2) return;
    const badgedIds = new Set([...badges.keys()].map(k => k.split(':')[1]));
    const techHeaders = TECH_DEFS.map(def => ({
      label: def.label, unit: def.unit === 'mph' ? speedUnit : def.unit,
    }));
    const exportRows = displayRows.map(r => ({
      label: r.corner.label || `T${r.corner.id}`,
      cells: r.cells.map(cl => ({
        text: fmtDelta(cl.total), value: cl.total,
        badge: badges.get(`${cl.driver.key}:${r.corner.id}`) || null,
      })),
      detail: badgedIds.has(String(r.corner.id)) ? {
        entryExit: r.cells.map(cl => ({
          name: cl.driver.name, accent: cl.driver.accent,
          entryText: fmtDelta(cl.entry), entryVal: cl.entry,
          exitText: fmtDelta(cl.exit), exitVal: cl.exit,
        })),
        // Speed profile through the corner, y pre-converted to display units.
        spark: [ref, ...others]
          .map(d => ({ accent: d.accent, pts: phaseFor(d, r.corner.id)?.trace }))
          .filter(s => Array.isArray(s.pts) && s.pts.length >= 3)
          .map(s => ({ accent: s.accent, pts: s.pts.map(([x, y]) => [x, convertValue(y, 'mph', unitPrefs)]) })),
        techHeaders,
        techRows: [ref, ...others].map(d => ({
          name: d.name, accent: d.accent,
          cells: TECH_DEFS.map(def => techCellData(d, r.corner.id, def)),
        })),
      } : null,
    }));
    const sig = ready.map(d => {
      const { env, coast, release, medianTaper } = signature(d);
      const stats = [];
      if (env) {
        stats.push({ label: 'brake', value: `${env.max_brake_g?.toFixed(2) ?? '—'}G` });
        stats.push({ label: 'lat',   value: `${env.max_lat_g?.toFixed(2) ?? '—'}G` });
        stats.push({ label: 'top',   value: `${fmtSpeed(env.max_speed)} ${speedUnit}` });
      }
      if (coast != null) stats.push({ label: 'coast', value: `${coast.toFixed(1)}s` });
      if (release) stats.push({ label: 'release',
        value: `${release}${medianTaper != null ? ` · ${Math.round(medianTaper)} m` : ''}` });
      return { name: d.name, accent: d.accent, stats };
    });
    const slug = v => (v || '').replace(/[^\w-]+/g, '_').replace(/^_+|_+$/g, '');
    const host = window.location.hostname;
    exportHeadToHeadPng({
      title: [trackName, sessionDate].filter(Boolean).join(' · ') || 'Head-to-Head',
      subtitle: `Head-to-Head — best lap vs best lap, seconds relative to ${ref.name}`,
      watermark: /^(localhost|127\.|\d+\.\d+\.)/.test(host) ? 'TrackDay Analysis' : host,
      themeFrom: panelRef.current,
      refName: ref.name,
      scoreboard: [...ready]
        .sort((a, b) => a.best.trace.lap_time_seconds - b.best.trace.lap_time_seconds)
        .map(d => ({
          name: d.name, accent: d.accent, isRef: d === ref,
          lapLabel: `L${d.lapNo} · ${d.best.trace.lap_time_str || d.best.trace.lap_time_seconds.toFixed(3)}`,
          gapLabel: d === ref ? '' : `+${(d.best.trace.lap_time_seconds - ref.best.trace.lap_time_seconds).toFixed(3)}`,
        })),
      columns: others.map(d => ({ key: d.key, name: d.name, accent: d.accent })),
      rows: exportRows,
      straights: straightsRow
        ? { label: 'Straights',
            cells: straightsRow.cells.map(cl => ({ text: fmtDelta(cl.total), value: cl.total })) }
        : { label: 'Straights', cells: [] },
      maxAbs,
      signature: sig,
      filename: `${[slug(trackName) || 'track', slug(sessionDate), 'headtohead'].filter(Boolean).join('_')}.png`,
    }).catch(err => console.error('Head-to-head export failed:', err));
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const anyCalculating = drivers.some(d => d.metricsLoading);

  return (
    <div className={styles.panel} ref={panelRef}>
      <PanelHeader className={styles.header} title={<span className={styles.title}>Head-to-Head</span>}>
        <div className={styles.opts}>
          {onAiSummary && ready.length >= 2 && rows.length > 0 && (
            <span className={styles.aiMenuWrap} ref={aiMenuRef}>
              <button
                className={`${styles.optBtn} ${styles.aiBtn}`}
                onClick={() => setAiMenuOpen(v => !v)}
                disabled={coachBusy}
                title="Ask the AI coach to explain this comparison for one of the drivers — where they win or lose time and what to change"
              >{coachBusy ? 'Coaching…' : 'Get AI Feedback ✨'}</button>
              {aiMenuOpen && (
                <div className={styles.aiMenu}>
                  <div className={styles.aiMenuHdr}>Coach which driver?</div>
                  {ready.map(d => (
                    <button
                      key={d.key}
                      className={styles.aiMenuItem}
                      onClick={() => runAiCoach({ key: d.key, name: d.name })}
                      title={d === ref
                        ? `${d.name} set the reference lap — the coaching covers where the other drivers are quicker`
                        : `Coaching for ${d.name}: where they lose time to ${ref.name}'s reference lap and what to change`}
                    >
                      <span className={styles.chip} style={{ background: d.accent }} />
                      For {d.name}
                      {d.key === 'p' && <span className={styles.aiMenuTag}>you</span>}
                      {d === ref && <span className={styles.aiMenuTag}>ref</span>}
                    </button>
                  ))}
                </div>
              )}
            </span>
          )}
          {onCoach && ready.length >= 2 && rows.length > 0 && (
            <button
              className={styles.optBtn}
              onClick={() => onCoach(buildHeadToHeadPayload({ ready, ref, rows, straightsRow, straightRows }))}
              title="Send this comparison to the AI coach — it explains where you lose time to the reference lap and what to change"
            >Coach ⚡</button>
          )}
          {on3DCompare && ready.length >= 1 && (
            <button
              className={styles.optBtn}
              // `car` is deliberately omitted — the backend fills it from the
              // session's own metadata, which is the authoritative name.
              onClick={() => on3DCompare(ready.slice(0, 4).map((d, k) => ({
                session_id: d.session.session_id,
                lap_index: d.best.lapIndex,
                name: d.name,
                color: d.accent || LAP_COLORS[k % LAP_COLORS.length],
              })))}
              title="Watch these laps race each other in 3D — the track is derived from the GPS traces, and the pavement is colored by throttle and brake"
            >3D ⛰</button>
          )}
          {ready.length >= 2 && !sfMismatch && (
            <button
              className={`${styles.optBtn} ${trackDifferenceOpen ? styles.optActive : ''}`}
              onClick={() => setTrackDifferenceOpen(value => !value)}
              title="Replay the selected drivers at the same position around the circuit and see who carries more speed"
            >Track {trackDifferenceOpen ? '▴' : '▾'}</button>
          )}
          {ready.length >= 2 && rows.length > 0 && (
            <button
              className={styles.optBtn}
              onClick={handleExportPng}
              title="Download this comparison as a PNG report — the top time-loss corners are exported expanded with their technique details"
            >PNG ⤓</button>
          )}
          {rows.length > 1 && (
            <button
              className={`${styles.optBtn} ${sortByLoss ? styles.optActive : ''}`}
              onClick={() => setSortByLoss(v => !v)}
              title="Order corners by the largest time difference (gain or loss) instead of track order"
            >Biggest first</button>
          )}
          {anyCalculating && <span className={styles.calcNote}>computing metrics…</span>}
          <HelpButton panel="headtohead" page="sessions" className={styles.optBtn} />
        </div>
      </PanelHeader>

      <div className={styles.body}>
        {ready.length < 2 && (
          <div className={styles.hint}>
            Load a second session (<b>+ Add Session</b> in the lap list) and select laps for
            each driver to compare them corner by corner.
          </div>
        )}

        {ready.length >= 2 && sfMismatch && (
          <div className={styles.hint} style={{ color: '#f5c842' }}>
            ⚠ These sessions use different start/finish lines (~{Math.round(sfMismatch)} m
            apart), so per-corner deltas compare the wrong pieces of track.
            Re-align the flagged session from the lap list first.
          </div>
        )}

        {ready.length >= 2 && (
          <>
            {/* AI coach card — streams the coaching feedback in place */}
            {coach && (
              <div className={styles.coachCard}>
                <div className={styles.coachHeader}>
                  <span className={styles.coachTitle}>
                    AI Coach{coach.focus?.name ? ` — for ${coach.focus.name}` : ''}
                  </span>
                  {coach.status === 'streaming' && <span className={styles.coachNote}>streaming…</span>}
                  <span className={styles.coachActions}>
                    {coachBusy ? (
                      <button className={styles.optBtn} onClick={stopCoach}>Stop</button>
                    ) : (
                      <button className={styles.optBtn} onClick={() => runAiCoach(coach.focus)}
                        title="Ask again for the same driver">Regenerate</button>
                    )}
                    <button className={styles.optBtn} onClick={dismissCoach} title="Close the AI coach">✕</button>
                  </span>
                </div>
                <div className={styles.coachBody} ref={coachBodyRef}>
                  {coach.status === 'error' ? (
                    <div className={styles.coachErr}>
                      The AI coach is unavailable right now — please try again in a few minutes.
                    </div>
                  ) : coach.text ? (
                    // One card per coaching section (corner/topic) so a wide
                    // panel lays them out side by side instead of one narrow
                    // left-aligned column.
                    <div className={styles.coachGrid}>
                      {splitMarkdownSections(coach.text).map((s, i) => (
                        <MiniMarkdown key={i} text={s} className={styles.coachProse} />
                      ))}
                    </div>
                  ) : (
                    <div className={styles.coachNote}>Analyzing your comparison…</div>
                  )}
                </div>
              </div>
            )}

            {/* Scoreboard */}
            <table className={styles.scoreboard}>
              <tbody>
                {[...ready].sort((a, b) => a.best.trace.lap_time_seconds - b.best.trace.lap_time_seconds)
                  .map(d => (
                  <tr key={d.key}>
                    <td><span className={styles.chip} style={{ background: d.accent }} /></td>
                    <td className={styles.scoreName}>{d.name}</td>
                    <td className={styles.scoreLap}>L{d.lapNo} · {d.best.trace.lap_time_str || d.best.trace.lap_time_seconds.toFixed(3)}</td>
                    <td className={styles.scoreGap}>
                      {d === ref
                        ? <span className={styles.refTag}
                            title="Reference lap — the fastest of the compared laps; every delta below is measured against it">ref</span>
                        : <span className={styles.loss}
                            title={`${d.name}'s best selected lap is ${(d.best.trace.lap_time_seconds - ref.best.trace.lap_time_seconds).toFixed(3)}s slower than ${ref.name}'s`}
                          >+{(d.best.trace.lap_time_seconds - ref.best.trace.lap_time_seconds).toFixed(3)}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {trackDifferenceOpen && trackLapA && trackLapB && (
              <div className={styles.trackDifferenceBlock}>
                <div className={styles.trackDifferenceBar}>
                  <span>Track Difference</span>
                  {trackOpponents.length > 1 && (
                    <label>
                      Compare with
                      <select value={trackDriverB.key} onChange={event => setTrackOpponentKey(event.target.value)}>
                        {trackOpponents.map(driver => (
                          <option key={driver.key} value={driver.key}>{driver.name} · L{driver.lapNo}</option>
                        ))}
                      </select>
                    </label>
                  )}
                  {onOpenTrackDifference && (
                    <button
                      type="button"
                      className={styles.optBtn}
                      onClick={() => onOpenTrackDifference({
                        a: { sessionId: trackLapA.sessionId, lapIndex: trackLapA.lapIndex },
                        b: { sessionId: trackLapB.sessionId, lapIndex: trackLapB.lapIndex },
                      })}
                    >Open as panel ↗</button>
                  )}
                </div>
                <TrackDifferenceView
                  compact
                  lapA={trackLapA}
                  lapB={trackLapB}
                  sectors={sectors}
                  elevationProfile={elevationProfile}
                  unitPrefs={unitPrefs}
                />
              </div>
            )}

            {/* Time won/lost per corner */}
            {rows.length > 0 && (
              <table className={styles.deltaTable}>
                <thead>
                  <tr>
                    <th
                      className={styles.cornerCol}
                      title={`Seconds through each corner relative to ${ref.name}'s reference lap. Red / + = time lost to ${ref.name}; green / − = time gained. All rows (incl. Straights) sum to the driver's total gap. Click a row for the entry/exit split and technique details.`}
                    >Time vs {ref.name}</th>
                    {others.map(d => (
                      <th key={d.key}
                        title={`${d.name}'s time gained/lost vs ${ref.name}, corner by corner (seconds)`}>
                        <span className={styles.chip} style={{ background: d.accent }} /> {d.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map(r => (
                    <FragmentRow
                      key={r.corner.id}
                      row={r} refDriver={ref} others={others}
                      badges={badges} maxAbs={maxAbs}
                      expanded={expandedCorner === r.corner.id}
                      onToggle={() => setExpandedCorner(v => v === r.corner.id ? null : r.corner.id)}
                      techCell={techCell}
                      speedUnit={speedUnit}
                      unitPrefs={unitPrefs}
                    />
                  ))}
                  {straightsRow && (
                    <>
                      <tr
                        className={`${styles.straightsRow} ${straightRows.length ? styles.straightsExpandable : ''}`}
                        onClick={straightRows.length
                          ? () => setExpandedCorner(v => v === 'straights' ? null : 'straights')
                          : undefined}
                      >
                        <td className={styles.cornerCol}
                          title="Everything outside the corner bands (straights) plus any measurement residual — computed as the total lap gap minus the corner rows, so each column sums to the scoreboard gap. Click to break it down straight by straight.">
                          {straightRows.length > 0 && (
                            <span className={styles.expander}>{expandedCorner === 'straights' ? '▾' : '▸'}</span>
                          )}
                          Straights</td>
                        {straightsRow.cells.map(cl => (
                          <DeltaCell key={cl.driver.key} delta={cl.total}
                            title={`${cl.driver.name} vs ${ref.name} outside the corners: ${fmtDelta(cl.total)}s`}
                            barFrac={Math.abs(cl.total ?? 0) / maxAbs} />
                        ))}
                      </tr>
                      {expandedCorner === 'straights' && straightRows.map(t => (
                        <tr key={t.label} className={styles.straightSubRow}>
                          <td className={styles.cornerCol}>
                            <span className={styles.straightSubLabel}>{t.label}</span>
                            <span className={styles.straightLen}>{Math.round(t.len_m)} m</span>
                            <button
                              className={styles.zoomBtn}
                              title="Zoom the traces and the track map to this straight"
                              onClick={(e) => { e.stopPropagation(); zoomToRow(t); }}
                            >⌖</button>
                          </td>
                          {t.cells.map(cl => (
                            <DeltaCell key={cl.driver.key} delta={cl.total}
                              title={`${cl.driver.name} vs ${ref.name} down the ${t.label} straight${t.isSF ? ' (crosses start/finish)' : ''}: ${fmtDelta(cl.total)}s — straight-line gaps usually come from exit speed out of ${t.label.split(' → ')[0]} or outright power`}
                              barFrac={Math.abs(cl.total ?? 0) / maxAbs} />
                          ))}
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            )}

            {/* Driver signatures */}
            <div className={styles.sigStrip}>
              {ready.map(d => {
                const { env, coast, release, medianTaper } = signature(d);
                return (
                  <div key={d.key} className={styles.sigRow}>
                    <span className={styles.chip} style={{ background: d.accent }} />
                    <span className={styles.sigName}>{d.name}</span>
                    {env ? (
                      <>
                        <span className={styles.sigStat} title="Peak braking deceleration across the analysed laps">brake <b>{env.max_brake_g?.toFixed(2) ?? '—'}G</b></span>
                        <span className={styles.sigStat} title="Peak lateral (cornering) G across the analysed laps">lat <b>{env.max_lat_g?.toFixed(2) ?? '—'}G</b></span>
                        <span className={styles.sigStat} title="Highest speed reached across the analysed laps">top <b>{fmtSpeed(env.max_speed)} {speedUnit}</b></span>
                      </>
                    ) : <span className={styles.sigStat}>{d.metricsLoading ? 'computing…' : 'no metrics'}</span>}
                    {coast != null && <span className={styles.sigStat} title="Total seconds neither braking nor accelerating, summed over all corners of the best lap — the classic time leak">coast <b>{coast.toFixed(1)}s</b></span>}
                    {release && (
                      <span className={styles.sigStat}
                        title="Brake-release character: median metres over which braking tapers from peak to fully released, across corners of the best lap. >22 m = progressive (smooth trail-off), <10 m = abrupt (snapped off, unsettles the car)">
                        release <b>{release}{medianTaper != null ? ` · ${Math.round(medianTaper)} m` : ''}</b>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Inline speed-vs-distance profile through one corner: each driver's best-lap
// phase trace (RDP-simplified [[m from apex, mph]] — already in the metrics
// payload), apex marked at x = 0.
function CornerSparkline({ series, unitPrefs }) {
  const W = 380, H = 96, PADL = 36, PADR = 10, PADT = 8, PADB = 16;
  const xs = series.flatMap(s => s.pts.map(p => p[0]));
  const ys = series.flatMap(s => s.pts.map(p => p[1]));
  const x0 = Math.min(...xs), x1 = Math.max(...xs);
  let y0 = Math.min(...ys), y1 = Math.max(...ys);
  if (y1 - y0 < 2) { y0 -= 1; y1 += 1; }
  const X = v => PADL + ((v - x0) / (x1 - x0 || 1)) * (W - PADL - PADR);
  const Y = v => PADT + (1 - (v - y0) / (y1 - y0)) * (H - PADT - PADB);
  const cv = v => Math.round(convertValue(v, 'mph', unitPrefs));
  return (
    <svg className={styles.spark} width={W} height={H} viewBox={`0 0 ${W} ${H}`}
      role="img" aria-label="Speed through this corner per driver">
      {x0 < 0 && x1 > 0 && (
        <>
          <line x1={X(0)} x2={X(0)} y1={PADT} y2={H - PADB} className={styles.sparkApex} />
          <text x={X(0)} y={H - 4} className={styles.sparkLabel} textAnchor="middle">apex</text>
        </>
      )}
      {series.map(s => (
        <polyline
          key={s.key}
          fill="none" stroke={s.accent} strokeWidth="1.6" strokeLinejoin="round"
          points={s.pts.map(p => `${X(p[0]).toFixed(1)},${Y(p[1]).toFixed(1)}`).join(' ')}
        />
      ))}
      <text x={PADL - 5} y={Y(y1) + 4} className={styles.sparkLabel} textAnchor="end">{cv(y1)}</text>
      <text x={PADL - 5} y={Y(y0) + 2} className={styles.sparkLabel} textAnchor="end">{cv(y0)}</text>
    </svg>
  );
}

// A corner row + its optional expanded technique block (kept as a component so
// the table stays a flat <tbody>).
function FragmentRow({ row, refDriver, others, badges, maxAbs, expanded, onToggle, techCell, speedUnit, unitPrefs }) {
  const spark = expanded
    ? [refDriver, ...others]
        .map(d => ({ key: d.key, accent: d.accent, pts: phaseFor(d, row.corner.id)?.trace }))
        .filter(s => Array.isArray(s.pts) && s.pts.length >= 3)
    : [];
  return (
    <>
      <tr
        className={styles.cornerRow}
        onClick={onToggle}
        onMouseEnter={() => publishCornerHighlight(rowHighlightDetail(row))}
        onMouseLeave={() => publishCornerHighlight(null)}
      >
        <td className={styles.cornerCol}>
          <span className={styles.expander}>{expanded ? '▾' : '▸'}</span>
          {row.corner.label || `T${row.corner.id}`}
          <button
            className={styles.zoomBtn}
            title="Zoom the speed/G/delta traces and the track map to this corner"
            onClick={(e) => { e.stopPropagation(); zoomToRow(row); }}
          >⌖</button>
        </td>
        {row.cells.map(cl => (
          <DeltaCell
            key={cl.driver.key}
            delta={cl.total}
            badge={badges.get(`${cl.driver.key}:${row.corner.id}`)}
            barFrac={Math.abs(cl.total ?? 0) / maxAbs}
            title={cl.total == null ? undefined
              : `${cl.driver.name} ${cl.total > 0 ? 'loses' : 'gains'} ${Math.abs(cl.total).toFixed(3)}s vs ${refDriver.name} through ${row.corner.label || 'this corner'} — click for entry/exit split`}
          />
        ))}
      </tr>
      {expanded && (
        <tr className={styles.detailRow}>
          <td colSpan={1 + row.cells.length}>
            <div className={styles.entryExit}>
              {row.cells.map(cl => (
                <span key={cl.driver.key} className={styles.entryExitItem}
                  title={`${cl.driver.name} vs ${refDriver.name}: entry = corner entry → apex, exit = apex → corner exit (split at the reference lap's apex). Entry losses point at braking/turn-in; exit losses at apex speed and throttle.`}>
                  <span className={styles.chip} style={{ background: cl.driver.accent }} />
                  entry <b className={cl.entry > 0 ? styles.loss : styles.gain}>{fmtDelta(cl.entry)}</b>
                  {' · '}exit <b className={cl.exit > 0 ? styles.loss : styles.gain}>{fmtDelta(cl.exit)}</b>
                </span>
              ))}
            </div>
            {spark.length >= 2 && (
              <div
                className={styles.sparkWrap}
                title={`Speed through ${row.corner.label || 'this corner'} (${speedUnit}) — each driver's best lap, colored by driver; dashed line marks the apex`}
              >
                <CornerSparkline series={spark} unitPrefs={unitPrefs} />
              </div>
            )}
            <table className={styles.techTable}>
              <thead>
                <tr>
                  <th />
                  {TECH_DEFS.map(def => (
                    <th key={def.key} title={`${def.tip} (${def.unit === 'mph' ? speedUnit : def.unit})${
                      def.higherIsBetter == null ? '' : def.higherIsBetter ? ' — higher is better' : ' — lower is better'}`}>
                      {def.label}
                      <span className={styles.unitHint}> {def.unit === 'mph' ? speedUnit : def.unit}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[refDriver, ...others].map(d => (
                  <tr key={d.key}>
                    <td className={styles.techName}>
                      <span className={styles.chip} style={{ background: d.accent }} /> {d.name}
                    </td>
                    {TECH_DEFS.map(def => techCell(d, row.corner.id, def))}
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}
